import { CircularProgress, Stack, Typography } from '@mui/material'
import Autocomplete from '@mui/material/Autocomplete'
import TextField from '@mui/material/TextField'
import { getCodesDropdown } from 'api/accessCode'
import React, { SyntheticEvent, useCallback, useMemo, useState } from 'react'
import { useQuery } from 'react-query'
import { FilterModelNumberArray, Nullish, Option } from 'types'

export function useOrgStateUnitFilter() {
  const [selectedCodes, setSelectedCodes] = useState<Option[]>([])

  const query = useQuery(['codesDropdown'], () => getCodesDropdown({ onlyValid: false }))

  const options = useMemo(() => {
    if (!query.data) {
      return []
    }

    return query.data.data.map((item) => ({
      value: item.id,
      label: [item.specificState, item.specificUnitName, item.code].filter(Boolean).join('/'),
    })) as Option[]
  }, [query.data])

  const orgStateUnitFilterValue: Nullish<FilterModelNumberArray> = useMemo(() => {
    if (selectedCodes.length > 0) {
      return {
        type: 'number',
        op: 'in',
        value: selectedCodes.map((code) => code.value),
      } as FilterModelNumberArray
    }

    return null
  }, [selectedCodes])

  const handleChange = useCallback((_: SyntheticEvent, newValue: Option[]) => {
    setSelectedCodes(newValue)
  }, [])

  const OrgStateUnitFilter = useMemo(() => {
    return (
      <Stack direction="row" alignItems="center" className="gap-16">
        <Typography whiteSpace="nowrap">State/Unit/Access Code</Typography>
        <Autocomplete
          multiple
          options={options}
          loading={query.isLoading}
          value={selectedCodes}
          onChange={handleChange}
          openOnFocus
          fullWidth
          sx={{ minWidth: 320 }}
          renderInput={(params) => (
            <TextField
              {...params}
              size="small"
              name="orgStateUnit"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <React.Fragment>
                    {query.isLoading ? <CircularProgress color="inherit" size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </React.Fragment>
                ),
              }}
            />
          )}
        />
      </Stack>
    )
  }, [options, query.isLoading, selectedCodes, handleChange])

  return { OrgStateUnitFilter, orgStateUnitFilterValue }
}
